import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { 
  ChevronLeft, ChevronRight, Plus, 
  Calendar as CalendarIcon, Clock, X, AlertCircle, List, Edit, Trash2, ChevronDown, ChevronUp, MapPin, Filter, XCircle
} from 'lucide-react';
import { formatDate, getWeekRange, isToday } from '../../utils/dateUtils';
import JobForm from '../forms/JobForm';
import { getBusinessType } from '../../utils/businessTypeUtils';
import { getWorkers } from '../../redux/features/workerSlice';
import { getPartners } from '../../redux/features/partnerSlice';
import { getWorkerName, getWorkerInitials, getWorkerColor } from '../../utils/workerUtils';
import { fetchDeviceCategories } from '../../redux/api';
import DeadlineMarker from '../job/DeadlineMarker';
import { formatDisplayValue } from '../../utils/displayUtils';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { shouldApplyJobSocketPayload, getCurrentCompanyId } from '../../utils/tenantUtils';
import { isOutgoingHandoffJob } from '../../utils/handoffWorkflowUtils';
import { canUserCreateJobs, canUserDeleteJobs, getWorkerPermissionsFromStorage } from '../../utils/profileRoleUtils';
import { getJobs, updateJobInArray, deleteJob, refreshJobs } from '../../redux/features/jobSlice';
import moment from 'moment';

// Create half-hour time slots from 7 AM to 8 PM
const HOURS = [];
for (let hour = 7; hour < 20; hour++) {
  HOURS.push({ hour, minute: 0 });
  HOURS.push({ hour, minute: 30 });
}

// These will be translated in the component using useTranslation

const CalendarView = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { jobs = [] } = useSelector((state) => state.job || { jobs: [] });
  const { calendarJobs = [] } = useSelector((state) => state.dashboard || { calendarJobs: [] });
  const { workers = [] } = useSelector((state) => state.worker || { workers: [] });
  const { partners = [] } = useSelector((state) => state.partner || { partners: [] });
  const { userType } = useSelector((state) => state.auth || {});
  const { socket, isConnected } = useWebSocket();

  // Get effective user type (check localStorage first, then Redux)
  const effectiveUserType = useMemo(() => {
    if (typeof window !== 'undefined') {
      const storedUserType = localStorage.getItem('userType');
      if (storedUserType) {
        return storedUserType;
      }
    }
    return userType || 'company';
  }, [userType]);

  const workerPermissions = useMemo(() => getWorkerPermissionsFromStorage(), []);
  const canCreateJobs = canUserCreateJobs(effectiveUserType, workerPermissions);
  const canEditJobs = canCreateJobs;
  const canDeleteJobs = canUserDeleteJobs(effectiveUserType, workerPermissions);
  
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });
  
  // Filter states - now arrays to support multiple selections
  const [filters, setFilters] = useState({
    worker: [],
    deviceCategory: [],
    priority: []
  });
  const [deviceCategories, setDeviceCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // State declarations - moved before useEffect hooks that use them
  const [currentDate, setCurrentDate] = useState(moment());
  const [calendarView, setCalendarView] = useState('month');
  const [selectedDate, setSelectedDate] = useState(moment());
  const [showForm, setShowForm] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const weekDateInputRef = useRef(null);
  const modalSingleJobRef = useRef(null);
  const [expandedDate, setExpandedDate] = useState(null); // For mobile view - track which date is expanded
  const [showJobsModal, setShowJobsModal] = useState(false);
  const [modalDate, setModalDate] = useState(null);
  const [modalSingleJob, setModalSingleJob] = useState(null); // For single job modal in week view
  const [expandedJobId, setExpandedJobId] = useState(null); // Track which job has expanded details
  const [editingJob, setEditingJob] = useState(null); // Track job being edited

  // Load workers, partners, and device categories
  useEffect(() => {
    dispatch(getWorkers());
    dispatch(getPartners());
    
    const loadDeviceCategories = async () => {
      try {
        const response = await fetchDeviceCategories();
        if (response?.data) {
          setDeviceCategories(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error('Error fetching device categories:', error);
        setDeviceCategories([]);
      }
    };
    loadDeviceCategories();
  }, [dispatch]);

  // Kartični kalendar (drugi tab): učitaj poslove za vidljivu nedelju ili mesec + nezakazane
  useEffect(() => {
    const bt = getBusinessType();
    if (!bt) return;
    if (calendarView === 'month') {
      const start = currentDate.clone().startOf('month');
      const end = currentDate.clone().endOf('month');
      dispatch(
        getJobs({
          businessType: bt,
          startDate: start.toISOString(),
          endDate: end.toISOString(),
          includeUnscheduled: true,
        })
      );
    } else {
      const start = currentDate.clone().startOf('isoWeek');
      const end = currentDate.clone().endOf('isoWeek');
      dispatch(
        getJobs({
          businessType: bt,
          startDate: start.toISOString(),
          endDate: end.toISOString(),
          includeUnscheduled: true,
        })
      );
    }
  }, [dispatch, currentDate, calendarView]);

  useEffect(() => {
    modalSingleJobRef.current = modalSingleJob;
  }, [modalSingleJob]);

  // WebSocket: ažuriraj Redux kao CalendarWithDragDrop — bez GET /api/jobs na svaki tick
  useEffect(() => {
    if (socket && isConnected) {
      socket.on('job_updated', (data) => {
        if (!shouldApplyJobSocketPayload(data)) return;

        console.log('📋 CalendarView: Real-time job update received:', data);

        const m = modalSingleJobRef.current;
        if (m && data.jobId === m._id && data.status === 'Completed') {
          console.log('🎯 CalendarView: Job completed, closing modal');
          setModalSingleJob(null);
          setShowJobsModal(false);
        }

        if (data.job && data.job._id) {
          dispatch(updateJobInArray({ jobId: data.job._id, updates: data.job }));
        } else if (data.jobId) {
          const updates = {};
          if (data.status !== undefined) updates.status = data.status;
          if (data.travelStartTime !== undefined) updates.travelStartTime = data.travelStartTime;
          if (data.workStartTime !== undefined) updates.workStartTime = data.workStartTime;
          if (data.accumulatedTravelTime !== undefined) updates.accumulatedTravelTime = data.accumulatedTravelTime;
          if (data.accumulatedWorkTime !== undefined) updates.accumulatedWorkTime = data.accumulatedWorkTime;
          if (Object.keys(updates).length > 0) {
            dispatch(updateJobInArray({ jobId: data.jobId, updates }));
          }
        }
      });

      return () => {
        socket.off('job_updated');
      };
    }
  }, [socket, isConnected, dispatch]);
  
  const DAYS_OF_WEEK = [
    t('calendar.days.monday'), t('calendar.days.tuesday'), t('calendar.days.wednesday'), 
    t('calendar.days.thursday'), t('calendar.days.friday'), t('calendar.days.saturday'), 
    t('calendar.days.sunday')
  ];
  const DAYS_SHORT = [
    t('calendar.daysShort.mon'), t('calendar.daysShort.tue'), t('calendar.daysShort.wed'), 
    t('calendar.daysShort.thu'), t('calendar.daysShort.fri'), t('calendar.daysShort.sat'), 
    t('calendar.daysShort.sun')
  ];
  const MONTHS = [
    t('calendar.months.january'), t('calendar.months.february'), t('calendar.months.march'), 
    t('calendar.months.april'), t('calendar.months.may'), t('calendar.months.june'),
    t('calendar.months.july'), t('calendar.months.august'), t('calendar.months.september'), 
    t('calendar.months.october'), t('calendar.months.november'), t('calendar.months.december')
  ];
  const MONTHS_SHORT = [
    t('calendar.monthsShort.jan'), t('calendar.monthsShort.feb'), t('calendar.monthsShort.mar'), 
    t('calendar.monthsShort.apr'), t('calendar.monthsShort.may'), t('calendar.monthsShort.jun'),
    t('calendar.monthsShort.jul'), t('calendar.monthsShort.aug'), t('calendar.monthsShort.sep'), 
    t('calendar.monthsShort.oct'), t('calendar.monthsShort.nov'), t('calendar.monthsShort.dec')
  ];
  
  // Filter jobs based on filters
  const currentCompanyId = useMemo(() => getCurrentCompanyId(), []);

  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      // Exclude Archived, Completed, and Cancelled jobs - Finished jobs stay visible until archived
      if (job.status === 'Archived' || job.status === 'Completed' || job.status === 'Cancelled') return false;

      // Prosleđeni poslovi (pošiljalac) ne ostaju u kalendaru — vide se u „Prosleđeni poslovi”
      if (currentCompanyId && isOutgoingHandoffJob(job, currentCompanyId)) return false;
      
      // Only show scheduled jobs (with serviceDate)
      if (!job.serviceDate) return false;
      
      // Worker/Partner filter - check if job matches any selected worker/partner
      const matchesWorker = !filters.worker || (Array.isArray(filters.worker) ? filters.worker.length === 0 : !filters.worker) || (() => {
        if (!job.assignedTo || job.assignedTo === 'Unassigned' || job.assignedTo === '') return false;
        
        // Convert to array if it's a string (backward compatibility)
        const filterWorkerIds = Array.isArray(filters.worker) ? filters.worker : [filters.worker].filter(Boolean);
        if (filterWorkerIds.length === 0) return false;
        
        // Check if job matches any of the selected workers/partners
        return filterWorkerIds.some(filterWorkerId => {
          const workerId = String(filterWorkerId).trim();
          if (!workerId) return false;
          
          // Handle populated object
          if (typeof job.assignedTo === 'object' && job.assignedTo !== null) {
            const assignedToId = String(job.assignedTo._id || job.assignedTo.id || '').trim();
            if (assignedToId && assignedToId === workerId) return true;
            
            // Also check if it's a partner object
            if (job.assignedTo.companyName || job.assignedTo.name) {
              const selectedPartnerObj = partners.find(p => {
                const partnerId = String(p._id || p.id || '').trim();
                return partnerId === workerId;
              });
              if (selectedPartnerObj) {
                const selectedPartnerId = String(selectedPartnerObj._id || selectedPartnerObj.id || '').trim();
                if (selectedPartnerId === assignedToId) return true;
              }
            }
          }
          
          // Handle string ID (with or without prefix)
          const assignedToStr = String(job.assignedTo).trim();
          if (!assignedToStr || assignedToStr === 'Unassigned') return false;
          
          // Direct ID match
          if (assignedToStr === workerId) return true;
          
          // Check if assignedTo has worker: prefix
          if (assignedToStr.startsWith('worker:')) {
            const extractedWorkerId = assignedToStr.replace('worker:', '').trim();
            if (extractedWorkerId === workerId) return true;
          }
          
          // Check if assignedTo has partner: prefix
          if (assignedToStr.startsWith('partner:')) {
            const extractedPartnerId = assignedToStr.replace('partner:', '').trim();
            if (extractedPartnerId === workerId) return true;
          }
          
          // Check if it's a worker name match (legacy support)
          const selectedWorkerObj = workers.find(w => {
            const wId = String(w._id || w.id || '').trim();
            return wId === workerId;
          });
          if (selectedWorkerObj) {
            const workerName = `${selectedWorkerObj.firstName || ''} ${selectedWorkerObj.lastName || ''}`.trim();
            if (assignedToStr === workerName || 
                assignedToStr === selectedWorkerObj.firstName || 
                assignedToStr === selectedWorkerObj.lastName) {
              return true;
            }
          }
          
          // Check partner match
          const selectedPartnerObj = partners.find(p => {
            const partnerId = String(p._id || p.id || '').trim();
            return partnerId === workerId;
          });
          if (selectedPartnerObj) {
            const partnerIdStr = String(selectedPartnerObj._id || selectedPartnerObj.id || '').trim();
            if (assignedToStr === partnerIdStr) return true;
            if (assignedToStr === `partner:${partnerIdStr}`) return true;
          }
          
          return false;
        });
      })();
      
      // Device category filter - check if job matches any selected category
      const matchesDeviceCategory = !filters.deviceCategory || (Array.isArray(filters.deviceCategory) ? filters.deviceCategory.length === 0 : !filters.deviceCategory) || (() => {
        // Convert to array if it's a string (backward compatibility)
        const filterCategoryIds = Array.isArray(filters.deviceCategory) ? filters.deviceCategory : [filters.deviceCategory].filter(Boolean);
        if (filterCategoryIds.length === 0) return false;
        
        // Check if job matches any of the selected categories
        return filterCategoryIds.some(filterCategoryId => {
          if (job.deviceCategoryId) {
            const jobCategoryId = job.deviceCategoryId.toString();
            const filterId = filterCategoryId.toString();
            if (jobCategoryId === filterId) return true;
          }
          const selectedCategory = deviceCategories.find(cat => 
            (cat.id?.toString() === filterCategoryId.toString()) || 
            (cat._id?.toString() === filterCategoryId.toString())
          );
          if (selectedCategory && job.deviceCategoryName === selectedCategory.name) return true;
          return false;
        });
      })();
      
      // Priority filter - check if job matches any selected priority
      const matchesPriority = !filters.priority || (Array.isArray(filters.priority) ? filters.priority.length === 0 : !filters.priority) || (() => {
        // Convert to array if it's a string (backward compatibility)
        const filterPriorities = Array.isArray(filters.priority) ? filters.priority : [filters.priority].filter(Boolean);
        if (filterPriorities.length === 0) return false;
        return filterPriorities.includes(job.priority);
      })();
      
      // OR logic: show jobs that match ANY of the selected filters
      // If no filters are selected, show all jobs
      const hasAnyFilter = (Array.isArray(filters.worker) ? filters.worker.length > 0 : filters.worker) ||
                          (Array.isArray(filters.deviceCategory) ? filters.deviceCategory.length > 0 : filters.deviceCategory) ||
                          (Array.isArray(filters.priority) ? filters.priority.length > 0 : filters.priority);
      
      if (!hasAnyFilter) {
        // No filters selected, show all jobs
        return true;
      }
      
      // At least one filter is selected, show jobs that match ANY filter
      return matchesWorker || matchesDeviceCategory || matchesPriority;
    });
  }, [jobs, filters, deviceCategories, workers, partners, currentCompanyId]);

  // Group filtered jobs by date for efficient rendering
  const jobsByDate = useMemo(() => {
    const grouped = {};
    filteredJobs.forEach(job => {
      if (!job.serviceDate) return;
    const dateKey = moment(job.serviceDate).format('YYYY-MM-DD');
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(job);
    });
    return grouped;
  }, [filteredJobs]);

  // Count jobs by each filter criteria
  const getFilterCounts = useMemo(() => {
    const counts = {
      worker: 0,
      deviceCategory: 0,
      priority: 0
    };

    // Count by worker
    if (filters.worker) {
      counts.worker = jobs.filter(job => {
        if (job.status === 'Archived' || job.status === 'Finished' || job.status === 'Completed' || job.status === 'Cancelled') return false;
        if (!job.serviceDate) return false;
        if (!job.assignedTo) return false;
        
        const filterWorkerId = String(filters.worker);
        
        if (typeof job.assignedTo === 'object' && job.assignedTo !== null) {
          const assignedToId = String(job.assignedTo._id || job.assignedTo.id || '');
          return assignedToId === filterWorkerId;
    }
        
        const assignedToStr = String(job.assignedTo);
        if (assignedToStr === filterWorkerId) return true;
        if (assignedToStr.startsWith('worker:')) {
          const workerId = assignedToStr.replace('worker:', '');
          return workerId === filterWorkerId;
        }
        
        const selectedWorkerObj = workers.find(w => String(w._id) === filterWorkerId);
        if (selectedWorkerObj) {
          const workerName = `${selectedWorkerObj.firstName || ''} ${selectedWorkerObj.lastName || ''}`.trim();
          return assignedToStr === workerName || 
                 assignedToStr === selectedWorkerObj.firstName || 
                 assignedToStr === selectedWorkerObj.lastName;
        }
        
        return false;
      }).length;
    }

    // Count by device category
    if (filters.deviceCategory) {
      counts.deviceCategory = jobs.filter(job => {
        if (job.status === 'Archived' || job.status === 'Finished' || job.status === 'Completed' || job.status === 'Cancelled') return false;
        if (!job.serviceDate) return false;
        
        const filterCategoryId = filters.deviceCategory;
        if (job.deviceCategoryId) {
          const jobCategoryId = job.deviceCategoryId.toString();
          const filterId = filterCategoryId.toString();
          if (jobCategoryId === filterId) return true;
        }
        const selectedCategory = deviceCategories.find(cat => 
          (cat.id?.toString() === filterCategoryId.toString()) || 
          (cat._id?.toString() === filterCategoryId.toString())
        );
        if (selectedCategory && job.deviceCategoryName === selectedCategory.name) return true;
        return false;
      }).length;
    }

    // Count by priority
    if (filters.priority) {
      counts.priority = jobs.filter(job => {
        if (job.status === 'Archived' || job.status === 'Finished' || job.status === 'Completed' || job.status === 'Cancelled') return false;
        if (!job.serviceDate) return false;
        return job.priority === filters.priority;
      }).length;
    }

    return counts;
  }, [jobs, filters, deviceCategories, workers, partners]);

  // Handle touch events for swipe navigation
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      navigateNext();
    } else if (isRightSwipe) {
      navigatePrevious();
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Generate calendar days for month view
  const generateMonthDays = () => {
    const startDay = moment(currentDate).startOf('month').startOf('isoWeek'); // Use isoWeek for Monday start
    const endDay = moment(currentDate).endOf('month').endOf('isoWeek');
    const days = [];
    let day = startDay.clone();

    while (day.isSameOrBefore(endDay, 'day')) {
      days.push(day.clone());
      day.add(1, 'day');
    }

    return days;
  };
  
  // Calculate the week dates based on current date
  useEffect(() => {
    const weekDates = [];
    // Start week from Monday (isoWeek starts on Monday)
    const startOfWeek = moment(currentDate).startOf('isoWeek');
    
    // Generate dates for the week (Monday to Sunday)
    for (let i = 0; i < 7; i++) {
      weekDates.push(moment(startOfWeek).add(i, 'days').toDate());
    }
  }, [currentDate]);

  // Fetch jobs when date changes (isti Redux store kao scheduling tab)
  useEffect(() => {
    const businessType = getBusinessType();
    if (!businessType) return;

    let startDate;
    let endDate;
    if (calendarView === 'week' || calendarView === 'mobile') {
      const startOfWeek = moment(selectedDate).startOf('isoWeek');
      const endOfWeek = moment(startOfWeek).add(6, 'days');
      startDate = startOfWeek.toDate().toISOString();
      endDate = endOfWeek.endOf('day').toDate().toISOString();
    } else if (calendarView === 'month') {
      startDate = moment(currentDate).startOf('month').startOf('isoWeek').toDate().toISOString();
      endDate = moment(currentDate).endOf('month').endOf('isoWeek').toDate().toISOString();
    } else {
      startDate = moment(selectedDate).startOf('day').toDate().toISOString();
      endDate = moment(selectedDate).endOf('day').toDate().toISOString();
    }

    dispatch(
      getJobs({
        businessType,
        startDate,
        endDate,
        includeUnscheduled: true,
      })
    );
  }, [currentDate, selectedDate, calendarView, dispatch]);
  
  // Handle responsive view switching
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      setCalendarView(prev => {
        // Only switch to mobile if we're not already in mobile view
        if (isMobile && prev !== 'mobile') return 'mobile';
        // Switch to month view if we're moving from mobile to desktop
        if (!isMobile && prev === 'mobile') return 'month';
        return prev;
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Navigate through calendar
  const navigatePrevious = () => {
    if (calendarView === 'month') {
      setCurrentDate(moment(currentDate).subtract(1, 'month'));
    } else if (calendarView === 'week') {
      const newDate = moment(selectedDate).subtract(1, 'week');
      setSelectedDate(newDate);
      setCurrentDate(newDate);
    } else if (calendarView === 'day' || calendarView === 'mobile') {
      const newDate = moment(selectedDate).subtract(1, 'day');
      setSelectedDate(newDate);
      setCurrentDate(newDate);
    }
  };
  
  const navigateNext = () => {
    if (calendarView === 'month') {
      setCurrentDate(moment(currentDate).add(1, 'month'));
    } else if (calendarView === 'week') {
      const newDate = moment(selectedDate).add(1, 'week');
      setSelectedDate(newDate);
      setCurrentDate(newDate);
    } else if (calendarView === 'day' || calendarView === 'mobile') {
      const newDate = moment(selectedDate).add(1, 'day');
      setSelectedDate(newDate);
      setCurrentDate(newDate);
    }
  };
  
  const goToToday = () => {
    const today = moment();
    setCurrentDate(today);
    setSelectedDate(today);
  };
  
  // Get jobs for a specific date
  const getJobsForDate = (date) => {
    const dateKey = date.format('YYYY-MM-DD');
    return jobsByDate[dateKey] || [];
  };
  
  // Determine if a day has jobs
  const hasJobs = (date) => {
    return getJobsForDate(date).length > 0;
  };
  
  // Format time slot for display in 24-hour format (07:00, 07:30, etc)
  const formatTimeSlot = (timeSlot) => {
    const { hour, minute } = timeSlot;
    const displayHour = hour.toString().padStart(2, '0');
    const displayMinute = minute === 0 ? '00' : '30';
    return `${displayHour}:${displayMinute}`;
  };

  // Format date in European format (DD/MM/YYYY)
  const formatDateEU = (date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Handle click on a time slot
  const handleTimeSlotClick = (date, timeSlot) => {
    if (isSlotBooked(date, timeSlot)) {
      setErrorMessage(t('calendar.timeSlotBooked'));
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    
    setErrorMessage('');
    const selectedDateTime = date.clone();
    selectedDateTime.hour(timeSlot.hour).minute(timeSlot.minute);
    
    const formattedTime = `${timeSlot.hour.toString().padStart(2, '0')}:${timeSlot.minute === 0 ? '00' : '30'}`;
    
    setSelectedSlot({
      date: selectedDateTime.toDate(),
      formattedDate: selectedDateTime.format('YYYY-MM-DD'),
      formattedTime,
      displayDate: selectedDateTime.format('DD.MM.YYYY'),
      displayTime: formattedTime
    });
    
    setShowForm(true);
  };
  
  // Check if a time slot is already booked
  const isSlotBooked = (date, timeSlot) => {
    const timeSlotJobs = getJobsForTimeSlot(date, timeSlot);
    return timeSlotJobs.length > 0;
  };
  
  // Get jobs for a specific date and time slot
  const getJobsForTimeSlot = (date, timeSlot) => {
    const regularJobs = Array.isArray(filteredJobs) ? filteredJobs.filter(job => {
      if (!job || !job.serviceDate) return false;
      
      const jobDate = moment(job.serviceDate);
      if (!job.scheduledTime) return false;
      
      const [jobHour, jobMinute] = job.scheduledTime.split(':').map(Number);
      
      return (
        jobDate.isSame(date, 'day') &&
        jobHour === timeSlot.hour &&
        jobMinute === timeSlot.minute
      );
    }) : [];
    
    const calEvents = Array.isArray(calendarJobs) ? calendarJobs.filter(event => {
      if (!event || !event.start) return false;
      
      const eventStart = moment(event.start);
      
      return (
        eventStart.isSame(date, 'day') &&
        eventStart.hour() === timeSlot.hour &&
        eventStart.minute() === timeSlot.minute
      );
    }) : [];
    
    return [...regularJobs, ...calEvents];
  };
  
  // Close job form
  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedSlot(null);
    setErrorMessage('');
  };
  
  // Update isToday function to work with moment objects
  const isToday = (date) => {
    return date.isSame(moment(), 'day');
  };

  // Update isSelected function to work with moment objects
  const isSelected = (date) => {
    return selectedDate && date.isSame(selectedDate, 'day');
  };
  
  // Render the month view calendar
  const renderMonthView = () => {
    const days = generateMonthDays();
    const weeks = [];
    const daysInWeek = 7;

    for (let i = 0; i < days.length; i += daysInWeek) {
      weeks.push(days.slice(i, i + daysInWeek));
    }

    return (
      <div className="calendar-month bg-white rounded-lg">
        {/* Day headers */}
        <div className="grid grid-cols-7 text-center border-b">
          {DAYS_SHORT.map(day => (
            <div 
              key={day} 
              className="py-3 font-semibold text-sm text-gray-600"
              style={{ 
                writingMode: 'horizontal-tb', 
                textOrientation: 'mixed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                whiteSpace: 'nowrap',
                transform: 'none',
                letterSpacing: 'normal',
                direction: 'ltr',
                unicodeBidi: 'normal'
              }}
            >
              <span style={{ display: 'inline-block', transform: 'none' }}>{day}</span>
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {weeks.map((week, weekIdx) => (
            <React.Fragment key={weekIdx}>
              {week.map((date, dayIdx) => {
                const isCurrentMonth = date.month() === currentDate.month();
                const isToday = date.isSame(moment(), 'day');
                const isSelected = date.isSame(selectedDate, 'day');
                const dateJobs = getJobsForDate(date);
                
                return (
                  <div
                    key={dayIdx}
                    className={`
                      min-h-[120px] p-2 relative cursor-pointer border-b border-r
                      ${isCurrentMonth ? 'bg-white' : 'bg-gray-50'}
                      ${isToday ? 'bg-blue-50' : ''}
                      ${isSelected ? 'ring-2 ring-inset ring-blue-500' : ''}
                      hover:bg-gray-50 transition-colors
                    `}
                    onClick={() => {
                      setSelectedDate(date);
                      if (dateJobs.length > 0) {
                        setModalDate(date);
                        setShowJobsModal(true);
                      } else if (window.innerWidth < 768) {
                        setCalendarView('mobile');
                      }
                    }}
                  >
                    <div className="flex items-center justify-center mb-2">
                      <div className={`
                        flex items-center justify-center w-7 h-7 rounded-full
                        ${isToday ? 'bg-blue-500 text-white' : ''}
                        ${isSelected && !isToday ? 'bg-blue-500/20' : ''}
                      `}>
                        <span className={`
                          text-sm font-medium
                          ${isCurrentMonth ? (isToday ? 'text-white' : 'text-gray-900') : 'text-gray-400'}
                        `}>
                          {date.date()}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-center gap-1 mt-2">
                      {dateJobs.length > 0 ? (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setModalDate(date);
                              setShowJobsModal(true);
                            }}
                            className="text-sm font-bold text-red-600 hover:text-red-700 hover:underline cursor-pointer transition-colors"
                            title={t('calendar.viewJobs')}
                          >
                            {dateJobs.length}
                          </button>
                        </>
                      ) : (
                        <div className="text-sm text-gray-400">
                          -
                        </div>
                      )}
                      {isCurrentMonth && canCreateJobs && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCreateJob(date);
                          }}
                          className="p-1.5 bg-green-500 hover:bg-green-600 rounded text-white transition-colors"
                          title={t('jobs.createJob')}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };
  
  // Render the mobile card view
  const renderMobileCardView = () => {
    const selectedDateJobs = getJobsForDate(selectedDate);
    const formattedDate = selectedDate.format('dddd, MMMM D, YYYY');
    
    return (
      <div className="p-4 space-y-4">
        {/* Header with date and create button */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">{formattedDate}</h2>
          {canCreateJobs && (
            <button
              onClick={() => handleCreateJob(selectedDate)}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Job
            </button>
          )}
        </div>

        {/* Jobs list */}
        {selectedDateJobs.length > 0 ? (
          <div className="space-y-3">
            {selectedDateJobs.map((job, index) => (
              <div
                key={job._id || index}
                className="p-4 bg-white rounded-lg shadow-sm border border-gray-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">{formatDisplayValue(job.clientName)}</h3>
                    <p className="text-sm text-gray-600">{job.serviceType}</p>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="w-4 h-4 mr-1" />
                    {moment(job.serviceDate).format('HH:mm')}
                  </div>
                </div>
                {formatDisplayValue(job.issueDescription) !== '-' && (
                  <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                    {formatDisplayValue(job.issueDescription)}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-gray-500">
            <CalendarIcon className="w-12 h-12 mb-3" />
            <p className="text-center">No jobs scheduled for this day</p>
          </div>
        )}

        {/* Job creation modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black bg-opacity-50">
            <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-xl">
              <div className="flex items-center justify-between p-4 border-b">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{t('jobs.createNewJob')}</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedSlot ? `Adding job for ${selectedSlot.displayDate} at ${selectedSlot.displayTime}` : 'Select date and time'}
                  </p>
                </div>
                <button
                  onClick={handleCloseForm}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="p-6">
                <JobForm
                  isEdit={false}
                  jobData={{
                    serviceDate: selectedSlot?.formattedDate || '',
                    scheduledTime: selectedSlot?.formattedTime || '',
                    displayDateTime: selectedSlot ? `${selectedSlot.displayDate} ${selectedSlot.displayTime}` : ''
                  }}
                  onClose={handleCloseForm}
                  isModal={true}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  // Render the week view calendar
  const renderWeekView = () => {
    // Start week from Monday (moment's startOf('week') uses locale, but we want Monday)
    const startOfWeek = moment(selectedDate).startOf('isoWeek'); // isoWeek starts on Monday
    const weekDays = Array.from({ length: 7 }, (_, i) => moment(startOfWeek).add(i, 'days'));

    if (isMobile) {
      // Mobile: Grid layout - 3 rows x 2 columns, Sunday full width
    return (
        <div className="week-view-mobile">
          <div className="space-y-2">
            {/* First 6 days in 3 rows x 2 columns */}
            <div className="grid grid-cols-2 gap-2">
              {weekDays.slice(0, 6).map((day, index) => {
                const dayJobs = getJobsForDate(day);
                const isTodayDate = isToday(day);
                
                return (
            <div 
              key={day.format('YYYY-MM-DD')} 
                    className={`border border-gray-600 rounded-lg overflow-hidden flex flex-col ${
                      isTodayDate ? 'bg-gray-700/30 border-blue-500' : 'bg-gray-800'
              }`}
                  >
                    {/* Day Header */}
                    <div 
                      className={`p-2 border-b border-gray-600 cursor-pointer ${
                        isTodayDate ? 'bg-gray-700/50' : 'bg-gray-700'
                      }`}
                      onClick={() => {
                        if (dayJobs.length > 0) {
                          setModalDate(day);
                          setShowJobsModal(true);
                        } else {
                          setSelectedDate(day);
                        }
                      }}
            >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <div className="text-xs font-bold text-white">
                          {DAYS_SHORT[day.day() === 0 ? 6 : day.day() - 1]}
            </div>
                        <div className="text-sm font-bold text-white">
                          {day.format('D')}
                        </div>
                      </div>
                      {dayJobs.length > 0 && (
                        <div className="text-xs bg-blue-600 text-white rounded-full px-1.5 py-0.5 font-semibold">
                          {dayJobs.length}
                        </div>
                      )}
                    </div>
        </div>

                    {/* Jobs List - Compact */}
                    <div className="flex-1 p-1.5 overflow-y-auto min-h-[60px]">
                      {dayJobs.length > 0 ? (
                        <div className="space-y-1">
                          {dayJobs.map((job, idx) => (
                <div 
                              key={job._id || idx}
                              className="p-1.5 bg-gray-700 rounded text-xs cursor-pointer hover:bg-gray-600 transition-colors flex items-center gap-1 min-w-0"
                              onClick={() => {
                                setModalSingleJob(job);
                                setShowJobsModal(true);
                              }}
                            >
                              <span className="font-semibold text-white whitespace-nowrap flex-shrink-0 text-[10px]">
                                {job.scheduledTime ? (job.scheduledTime.length === 5 ? job.scheduledTime : job.scheduledTime.substring(0, 5)) : '—'}
                              </span>
                              <div className="font-semibold text-white truncate text-[10px] flex-1 min-w-0">
                                {formatDisplayValue(job.clientAddress || job.clientName)}
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                                {job.assignedTo && job.assignedTo !== 'Unassigned' && String(job.assignedTo).trim() !== '' && (
                                  <span
                                    className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold text-white"
                                    style={{ backgroundColor: getWorkerColor(job.assignedTo, workers), background: getWorkerColor(job.assignedTo, workers) }}
                                    title={getWorkerName(job.assignedTo, workers, partners)}
                                  >
                                    {getWorkerInitials(job.assignedTo, workers, partners)}
                                  </span>
                                )}
                                <DeadlineMarker job={job} size="sm" />
                              </div>
                </div>
              ))}
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 text-[10px] py-2">
                          {t('calendar.noJobs')}
            </div>
          )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Sunday - Full width */}
            {weekDays[6] && (() => {
              const day = weekDays[6];
            const dayJobs = getJobsForDate(day);
              const isTodayDate = isToday(day);
            
            return (
                <div 
                  key={day.format('YYYY-MM-DD')}
                  className={`border border-gray-600 rounded-lg overflow-hidden flex flex-col ${
                    isTodayDate ? 'bg-gray-700/30 border-blue-500' : 'bg-gray-800'
                  }`}
                >
                  {/* Day Header */}
                  <div 
                    className={`p-2 border-b border-gray-600 cursor-pointer ${
                      isTodayDate ? 'bg-gray-700/50' : 'bg-gray-700'
                    }`}
                    onClick={() => {
                      if (dayJobs.length > 0) {
                        setModalDate(day);
                        setShowJobsModal(true);
                      } else {
                      setSelectedDate(day);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <div className="text-xs font-bold text-white">
                          {DAYS_SHORT[day.day() === 0 ? 6 : day.day() - 1]}
                    </div>
                        <div className="text-sm font-bold text-white">
                      {day.format('D')}
                        </div>
                    </div>
                    {dayJobs.length > 0 && (
                        <div className="text-xs bg-blue-600 text-white rounded-full px-1.5 py-0.5 font-semibold">
                          {dayJobs.length}
                      </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Jobs List - Compact */}
                  <div className="flex-1 p-1.5 overflow-y-auto min-h-[60px]">
                    {dayJobs.length > 0 ? (
                      <div className="space-y-1">
                        {dayJobs.map((job, idx) => (
                          <div
                            key={job._id || idx}
                            className="p-1.5 bg-gray-700 rounded text-xs cursor-pointer hover:bg-gray-600 transition-colors flex items-center gap-1 min-w-0"
                            onClick={() => {
                              setModalSingleJob(job);
                              setShowJobsModal(true);
                            }}
                          >
                            <span className="font-semibold text-white whitespace-nowrap flex-shrink-0 text-[10px]">
                              {job.scheduledTime ? (job.scheduledTime.length === 5 ? job.scheduledTime : job.scheduledTime.substring(0, 5)) : '—'}
                            </span>
                            <div className="font-semibold text-white truncate text-[10px] flex-1 min-w-0">
                              {formatDisplayValue(job.clientAddress || job.clientName)}
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                              {job.assignedTo && job.assignedTo !== 'Unassigned' && String(job.assignedTo).trim() !== '' && (
                                <span
                                  className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold text-white"
                                  style={{ backgroundColor: getWorkerColor(job.assignedTo, workers), background: getWorkerColor(job.assignedTo, workers) }}
                                  title={getWorkerName(job.assignedTo, workers, partners)}
                                >
                                  {getWorkerInitials(job.assignedTo, workers, partners)}
                                </span>
                              )}
                              <DeadlineMarker job={job} size="sm" />
                            </div>
                          </div>
                        ))}
                  </div>
                ) : (
                      <div className="text-center text-gray-500 text-[10px] py-2">
                        {t('calendar.noJobs')}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      );
    }

    // Desktop: Original grid layout with time slots
    return (
      <div className="week-view">
        <div className="grid grid-cols-8 border-b border-gray-600">
          {/* Time column header */}
          <div className="p-2 text-center text-xs font-medium text-gray-300">{t('jobs.time')}</div>
          
          {/* Day headers */}
          {weekDays.map(day => (
            <div 
              key={day.format('YYYY-MM-DD')} 
              className={`p-2 text-center text-xs font-medium ${
                isToday(day) ? 'bg-gray-700' : ''
              }`}
              style={{ color: 'var(--calendar-text)' }}
            >
              <div 
                className="flex items-center justify-center"
                style={{ 
                  writingMode: 'horizontal-tb', 
                  textOrientation: 'mixed',
                  flexDirection: 'row',
                  whiteSpace: 'nowrap',
                  transform: 'none',
                  letterSpacing: 'normal'
                }}
              >
                {DAYS_SHORT[day.day() === 0 ? 6 : day.day() - 1]}
              </div>
              <div className="font-bold">{day.format('D')}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-8">
          {/* Time slots */}
          <div className="border-r border-gray-600">
            {HOURS.map((timeSlot, idx) => (
              <div 
                key={idx} 
                className="p-1 text-xs text-gray-400 h-8 border-b border-gray-600"
              >
                {formatTimeSlot(timeSlot)}
              </div>
            ))}
          </div>

          {/* Week grid */}
          {weekDays.map(day => {
            const dayJobs = getJobsForDate(day);
            
            return (
              <div key={day.format('YYYY-MM-DD')} className="relative">
                    {dayJobs.length > 0 && (
                      <div 
                        className="absolute top-1 right-1 z-10 text-xs bg-blue-600 text-white rounded-full px-2 py-1 font-semibold cursor-pointer hover:bg-blue-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          setModalDate(day);
                          setShowJobsModal(true);
                        }}
                        title={`${dayJobs.length} ${t('calendar.jobs')} - Kliknite za pregled`}
                      >
                        {dayJobs.length}
                      </div>
                    )}
                    {HOURS.map((timeSlot, idx) => {
                      const jobs = getJobsForTimeSlot(day, timeSlot);
                      const isSlotTaken = jobs.length > 0;
                      
                      return (
                        <div 
                          key={idx}
                          className={`p-1 h-8 border-r border-b border-gray-600 cursor-pointer
                            ${isToday(day) ? 'bg-gray-700/30' : ''}
                            ${isSlotTaken ? 'bg-blue-900/20' : 'hover:bg-gray-700/20'}
                          `}
                          onClick={() => {
                            if (isSlotTaken) {
                              // In week view, show only the first job from this time slot
                              const firstJob = jobs[0];
                              setModalSingleJob(firstJob);
                              setShowJobsModal(true);
                            } else {
                              handleTimeSlotClick(day, timeSlot);
                            }
                          }}
                        >
                          {jobs.map((job, i) => (
                            <div
                              key={i}
                              className="text-xs p-1 mb-1 rounded bg-blue-900/50 text-blue-200 truncate flex items-center gap-1"
                              title={`${formatDisplayValue(job.clientName)} - ${formatDisplayValue(job.serviceType || job.issueDescription)}`}
                            >
                              {job.assignedTo && job.assignedTo !== 'Unassigned' && String(job.assignedTo).trim() !== '' && (
                                <span
                                  className="flex-shrink-0 w-3 h-3 rounded-full flex items-center justify-center text-[7px] font-bold text-white"
                                  style={{ backgroundColor: getWorkerColor(job.assignedTo, workers), background: getWorkerColor(job.assignedTo, workers) }}
                                  title={getWorkerName(job.assignedTo, workers, partners)}
                                >
                                  {getWorkerInitials(job.assignedTo, workers, partners)}
                                </span>
                              )}
                              <DeadlineMarker job={job} size="sm" />
                              <span className="truncate">{job.scheduledTime ? `${job.scheduledTime} - ` : ''}{formatDisplayValue(job.clientName)}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render the day view calendar
  const renderDayView = () => {
    return (
      <div className="day-view">
        <div className="text-center mb-4">
          <div className="text-lg font-semibold" style={{ color: 'var(--calendar-text)' }}>
            {selectedDate.format('dddd, MMMM D, YYYY')}
          </div>
        </div>

        <div className="grid grid-cols-1">
          {HOURS.map((timeSlot, idx) => {
            const jobs = getJobsForTimeSlot(selectedDate, timeSlot);
            return (
              <div 
                key={idx}
                className="grid grid-cols-[100px_1fr] border-b border-gray-600"
              >
                <div className="p-2 text-xs text-gray-400">
                  {formatTimeSlot(timeSlot)}
                </div>
                <div 
                  className="p-1 min-h-[80px] cursor-pointer"
                  onClick={() => handleTimeSlotClick(selectedDate, timeSlot)}
                >
                  {jobs.map((job, i) => (
                    <div
                      key={i}
                      className="text-sm p-2 mb-1 rounded"
                      style={{
                        backgroundColor: 'var(--calendar-event-bg)',
                        color: 'var(--calendar-event-text)'
                      }}
                    >
                      <div className="font-medium">{job.title}</div>
                      {formatDisplayValue(job.clientName) !== '-' && (
                        <div className="text-xs opacity-75">{formatDisplayValue(job.clientName)}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Add new function to handle creating a job for selected date
  const handleCreateJob = (date) => {
    const selectedDateTime = moment(date);
    selectedDateTime.hour(9).minute(0); // Default to 09:00
    
    const formattedTime = '09:00';
    const formattedDate = selectedDateTime.format('YYYY-MM-DD');
    const displayDate = selectedDateTime.format('DD.MM.YYYY');
    
    setSelectedSlot({
      date: selectedDateTime.toDate(),
      formattedDate,
      formattedTime,
      displayDate,
      displayTime: formattedTime
    });
    
    setShowForm(true);
  };

  // Handle job edit
  const handleEditJob = (job) => {
    setEditingJob(job);
    setShowJobsModal(false);
    setModalSingleJob(null);
    setShowForm(true);
  };

  // Handle job delete
  const handleDeleteJob = (job) => {
    if (window.confirm(t('jobs.confirmDelete') || `Da li ste sigurni da želite obrisati posao za klijenta "${job.clientName}"?`)) {
      dispatch(deleteJob(job._id));
      dispatch(refreshJobs());
    }
  };

  // Toggle job details expansion
  const toggleJobDetails = (jobId) => {
    setExpandedJobId(expandedJobId === jobId ? null : jobId);
  };

  // Main render
  return (
    <div className="relative min-h-screen bg-gray-900 p-2 sm:p-4">
      {/* Header - Mobile Responsive */}
      <div className="mb-3 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
          <h1 className="text-base sm:text-2xl font-bold text-white">{t('calendar.title')}</h1>
          
          {/* View Mode Buttons - Mobile Responsive */}
          {!showForm && (
            <div className="flex flex-wrap gap-1 sm:gap-2 w-full sm:w-auto">
              <button
                onClick={() => setCalendarView('week')}
                className={`px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm rounded flex items-center ${
                  calendarView === 'week' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <span className="hidden sm:inline">{t('calendar.week')}</span>
                <span className="sm:hidden">{t('calendar.week')}</span>
              </button>
              <button
                onClick={() => setCalendarView('month')}
                className={`px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm rounded ${
                  calendarView === 'month' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <span className="hidden sm:inline">{t('calendar.month')}</span>
                <span className="sm:hidden">{t('calendar.month')}</span>
              </button>
              {canCreateJobs && (
                <button
                  onClick={() => handleCreateJob(selectedDate)}
                  className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm bg-green-600 text-white rounded hover:bg-green-700 flex items-center flex-shrink-0"
                >
                  <Plus className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2 flex-shrink-0" />
                  <span className="hidden sm:inline">{t('jobs.newJob')}</span>
                  <span className="sm:hidden ml-1 whitespace-nowrap">{t('jobs.newJob')}</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Calendar Content */}
      <div className="rounded-lg shadow-lg bg-gray-900">
        {!showForm && (
          <>
            {/* Navigation */}
            <div className="mb-4 p-2 sm:p-4 bg-gray-800 border-b border-gray-700">
              {/* Mobile Layout - Compact */}
              <div className="flex sm:hidden items-center gap-1.5 mb-2">
              <button
                onClick={navigatePrevious}
                  className="p-1 rounded-full hover:bg-gray-700 text-white transition-colors flex-shrink-0"
                aria-label="Prethodni"
              >
                  <ChevronLeft className="h-4 w-4" />
              </button>
                <div className="flex items-center gap-1 flex-1 justify-center min-w-0">
                {calendarView === 'week' ? (
                  // Show week range for week view - clickable to open date picker
                  <div className="relative flex-1">
                    <input
                      ref={weekDateInputRef}
                      type="date"
                      value={selectedDate.format('YYYY-MM-DD')}
                      lang="sr-RS"
                      onChange={(e) => {
                        const newDate = moment(e.target.value);
                        const startOfWeek = moment(newDate).startOf('isoWeek');
                        setSelectedDate(startOfWeek);
                        setCurrentDate(startOfWeek);
                      }}
                      className="absolute inset-0 opacity-0 cursor-pointer z-10"
                      style={{ width: '100%', height: '100%' }}
                    />
                    <div 
                      onClick={() => weekDateInputRef.current?.click()}
                      className="px-1.5 py-1 text-xs font-semibold text-white bg-gray-700 border border-gray-600 rounded text-center cursor-pointer hover:bg-gray-600 transition-colors"
                    >
                      {(() => {
                        const startOfWeek = moment(selectedDate).startOf('isoWeek');
                        const endOfWeek = moment(startOfWeek).add(6, 'days');
                        const startMonth = MONTHS_SHORT[startOfWeek.month()];
                        const endMonth = MONTHS_SHORT[endOfWeek.month()];
                        const startDay = startOfWeek.date();
                        const endDay = endOfWeek.date();
                        const year = startOfWeek.year();
                        
                        if (startMonth === endMonth) {
                          return `${startDay}-${endDay} ${startMonth} ${year}`;
                        } else {
                          return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
                        }
                      })()}
                    </div>
                  </div>
                ) : (
                <input
                  type={calendarView === 'month' ? 'month' : 'date'}
                  value={calendarView === 'month' 
                    ? currentDate.format('YYYY-MM')
                      : selectedDate.format('YYYY-MM-DD')
                  }
                  onChange={(e) => {
                    const newDate = moment(e.target.value);
                    setCurrentDate(newDate);
                    if (calendarView === 'day' || calendarView === 'mobile') {
                      setSelectedDate(newDate);
                    } else if (calendarView === 'week') {
                      // For week view, set selectedDate to the first day of the week
                        const startOfWeek = moment(newDate).startOf('isoWeek');
                      setSelectedDate(startOfWeek);
                        setCurrentDate(startOfWeek);
                    } else if (calendarView === 'month') {
                      // For month view, set selectedDate to first day of month
                      setSelectedDate(moment(newDate).startOf('month'));
                    }
                  }}
                  className={`px-1.5 py-1 text-xs font-semibold text-white bg-gray-700 border border-gray-600 rounded hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer flex-1 min-w-0 ${calendarView === 'month' ? 'text-center' : ''}`}
                  style={calendarView === 'month' ? { textAlign: 'center' } : {}}
                />
                )}
              </div>
                <button
                  onClick={navigateNext}
                  className="p-1 rounded-full hover:bg-gray-700 text-white transition-colors flex-shrink-0"
                  aria-label="Sledeći"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center justify-center p-1.5 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors flex-shrink-0"
                  title={t('jobs.filters')}
                >
                  <Filter className="h-3.5 w-3.5" />
                </button>
              </div>
              
              {/* Desktop Layout */}
              <div className="hidden sm:flex items-center justify-center gap-2 sm:gap-4 mb-3">
                <button
                  onClick={navigatePrevious}
                  className="p-1 sm:p-1.5 rounded-full hover:bg-gray-700 text-white transition-colors flex-shrink-0"
                  aria-label="Prethodni"
                >
                  <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <div className="flex items-center gap-1 sm:gap-2 flex-1 justify-center min-w-0">
                {calendarView === 'week' ? (
                  // Show week range for week view - clickable to open date picker
                  <div className="relative flex-1 sm:flex-initial">
                    <input
                      ref={weekDateInputRef}
                      type="date"
                      value={selectedDate.format('YYYY-MM-DD')}
                      lang="sr-RS"
                      onChange={(e) => {
                        const newDate = moment(e.target.value);
                        const startOfWeek = moment(newDate).startOf('isoWeek');
                        setSelectedDate(startOfWeek);
                        setCurrentDate(startOfWeek);
                      }}
                      className="absolute inset-0 opacity-0 cursor-pointer z-10"
                      style={{ width: '100%', height: '100%' }}
                    />
                    <div 
                      onClick={() => weekDateInputRef.current?.click()}
                      className="px-2 py-1.5 sm:px-3 sm:py-2 text-sm sm:text-lg font-semibold text-white bg-gray-700 border border-gray-600 rounded-md min-w-0 sm:min-w-[180px] text-center cursor-pointer hover:bg-gray-600 transition-colors"
                    >
                      {(() => {
                        const startOfWeek = moment(selectedDate).startOf('isoWeek');
                        const endOfWeek = moment(startOfWeek).add(6, 'days');
                        const startMonth = MONTHS[startOfWeek.month()];
                        const endMonth = MONTHS[endOfWeek.month()];
                        const startDay = startOfWeek.date();
                        const endDay = endOfWeek.date();
                        const year = startOfWeek.year();
                        
                        if (startMonth === endMonth) {
                          return `${startDay}-${endDay} ${startMonth} ${year}`;
                        } else {
                          return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
                        }
                      })()}
                    </div>
                  </div>
                ) : (
                <input
                  type={calendarView === 'month' ? 'month' : 'date'}
                  value={calendarView === 'month' 
                    ? currentDate.format('YYYY-MM')
                      : selectedDate.format('YYYY-MM-DD')
                  }
                  onChange={(e) => {
                    const newDate = moment(e.target.value);
                    setCurrentDate(newDate);
                    if (calendarView === 'day' || calendarView === 'mobile') {
                      setSelectedDate(newDate);
                    } else if (calendarView === 'week') {
                      // For week view, set selectedDate to the first day of the week
                        const startOfWeek = moment(newDate).startOf('isoWeek');
                      setSelectedDate(startOfWeek);
                        setCurrentDate(startOfWeek);
                    } else if (calendarView === 'month') {
                      // For month view, set selectedDate to first day of month
                      setSelectedDate(moment(newDate).startOf('month'));
                    }
                  }}
                  className={`px-2 py-1.5 sm:px-3 sm:py-2 text-sm sm:text-lg font-semibold text-white bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer flex-1 sm:flex-initial min-w-0 sm:min-w-[180px] ${calendarView === 'month' ? 'text-center' : ''}`}
                  style={calendarView === 'month' ? { textAlign: 'center' } : {}}
                />
                )}
              </div>
              <button
                onClick={navigateNext}
                  className="p-1 sm:p-1.5 rounded-full hover:bg-gray-700 text-white transition-colors flex-shrink-0"
                aria-label="Sledeći"
              >
                  <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-1 px-2 py-1.5 sm:px-3 sm:py-1.5 text-xs sm:text-sm bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors flex-shrink-0"
                  title={t('jobs.filters')}
                >
                  <Filter className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">{t('jobs.filters')}</span>
              </button>
              </div>

              {/* Filters Panel */}
              {showFilters && (
                <div className="bg-gray-700 rounded-lg p-3 sm:p-4 border border-gray-600">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs sm:text-sm font-semibold text-white">{t('jobs.filterJobs')}</h3>
                    <button
                      onClick={() => {
                        setFilters({ worker: [], deviceCategory: [], priority: [] });
                      }}
                      className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
                    >
                      <XCircle className="h-3 w-3" />
                      {t('jobs.clearFilters')}
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {/* Worker Filter - Multi-select with checkboxes */}
                    <div>
                      <label className="block text-xs text-gray-300 mb-2">{t('jobs.filterByWorker')}</label>
                      <div className="max-h-40 overflow-y-auto bg-gray-800 rounded border border-gray-600 p-2 space-y-1.5">
                        {workers.map((worker) => {
                          const isSelected = Array.isArray(filters.worker) ? filters.worker.includes(worker._id) : filters.worker === worker._id;
                          return (
                            <label key={worker._id} className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  const currentFilters = Array.isArray(filters.worker) ? filters.worker : (filters.worker ? [filters.worker] : []);
                                  const newFilters = e.target.checked
                                    ? [...currentFilters, worker._id]
                                    : currentFilters.filter(id => id !== worker._id);
                                  setFilters(prev => ({ ...prev, worker: newFilters }));
                                }}
                                className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                              />
                              <span>{worker.firstName} {worker.lastName}</span>
                            </label>
                          );
                        })}
                        {partners.map((partner) => {
                          const isSelected = Array.isArray(filters.worker) ? filters.worker.includes(partner._id) : filters.worker === partner._id;
                          return (
                            <label key={partner._id} className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  const currentFilters = Array.isArray(filters.worker) ? filters.worker : (filters.worker ? [filters.worker] : []);
                                  const newFilters = e.target.checked
                                    ? [...currentFilters, partner._id]
                                    : currentFilters.filter(id => id !== partner._id);
                                  setFilters(prev => ({ ...prev, worker: newFilters }));
                                }}
                                className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                              />
                              <span>{partner.name || partner.companyName}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Device Category Filter - Multi-select with checkboxes */}
                    <div>
                      <label className="block text-xs text-gray-300 mb-2">{t('jobs.filterByDeviceCategory')}</label>
                      <div className="max-h-40 overflow-y-auto bg-gray-800 rounded border border-gray-600 p-2 space-y-1.5">
                        {deviceCategories.map((category) => {
                          const categoryId = category.id || category._id;
                          const isSelected = Array.isArray(filters.deviceCategory) ? filters.deviceCategory.includes(categoryId) : filters.deviceCategory === categoryId;
                          return (
                            <label key={categoryId} className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  const currentFilters = Array.isArray(filters.deviceCategory) ? filters.deviceCategory : (filters.deviceCategory ? [filters.deviceCategory] : []);
                                  const newFilters = e.target.checked
                                    ? [...currentFilters, categoryId]
                                    : currentFilters.filter(id => id !== categoryId);
                                  setFilters(prev => ({ ...prev, deviceCategory: newFilters }));
                                }}
                                className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                              />
                              <span>{category.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Priority Filter - Multi-select with checkboxes */}
                    <div>
                      <label className="block text-xs text-gray-300 mb-2">{t('jobs.filterByPriority')}</label>
                      <div className="max-h-40 overflow-y-auto bg-gray-800 rounded border border-gray-600 p-2 space-y-1.5">
                        {['Low', 'Medium', 'High', 'Urgent'].map((priority) => {
                          const isSelected = Array.isArray(filters.priority) ? filters.priority.includes(priority) : filters.priority === priority;
                          return (
                            <label key={priority} className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  const currentFilters = Array.isArray(filters.priority) ? filters.priority : (filters.priority ? [filters.priority] : []);
                                  const newFilters = e.target.checked
                                    ? [...currentFilters, priority]
                                    : currentFilters.filter(p => p !== priority);
                                  setFilters(prev => ({ ...prev, priority: newFilters }));
                                }}
                                className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                              />
                              <span>{t(`jobs.priority${priority}`)}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Active Filters Display */}
                  {((Array.isArray(filters.worker) ? filters.worker.length > 0 : filters.worker) || 
                    (Array.isArray(filters.deviceCategory) ? filters.deviceCategory.length > 0 : filters.deviceCategory) || 
                    (Array.isArray(filters.priority) ? filters.priority.length > 0 : filters.priority)) && (
                    <div className="mt-3 pt-3 border-t border-gray-600">
                      <div className="flex flex-wrap gap-2">
                        {/* Worker/Partner filters */}
                        {Array.isArray(filters.worker) ? filters.worker.map(workerId => {
                          const worker = workers.find(w => String(w._id) === String(workerId));
                          const partner = partners.find(p => String(p._id) === String(workerId));
                          const name = worker ? `${worker.firstName} ${worker.lastName}` : (partner ? (partner.name || partner.companyName) : workerId);
                          return (
                            <span key={workerId} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                              {t('jobs.worker')}: {name}
                              <button
                                onClick={() => {
                                  const newFilters = filters.worker.filter(id => id !== workerId);
                                  setFilters(prev => ({ ...prev, worker: newFilters }));
                                }}
                                className="hover:text-gray-200"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          );
                        }) : filters.worker && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                            {t('jobs.worker')}: {getWorkerName(filters.worker, workers, partners)}
                            <button
                              onClick={() => setFilters(prev => ({ ...prev, worker: [] }))}
                              className="hover:text-gray-200"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        )}
                        
                        {/* Device Category filters */}
                        {Array.isArray(filters.deviceCategory) ? filters.deviceCategory.map(categoryId => {
                          const category = deviceCategories.find(cat => String(cat.id || cat._id) === String(categoryId));
                          return (
                            <span key={categoryId} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                              {t('jobs.category')}: {category?.name || categoryId}
                              <button
                                onClick={() => {
                                  const newFilters = filters.deviceCategory.filter(id => id !== categoryId);
                                  setFilters(prev => ({ ...prev, deviceCategory: newFilters }));
                                }}
                                className="hover:text-gray-200"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          );
                        }) : filters.deviceCategory && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                            {t('jobs.category')}: {deviceCategories.find(cat => (cat.id || cat._id) === filters.deviceCategory)?.name || filters.deviceCategory}
                            <button
                              onClick={() => setFilters(prev => ({ ...prev, deviceCategory: [] }))}
                              className="hover:text-gray-200"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        )}
                        
                        {/* Priority filters */}
                        {Array.isArray(filters.priority) ? filters.priority.map(priority => (
                          <span key={priority} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                            {t('jobs.priority')}: {t(`jobs.priority${priority}`)}
                            <button
                              onClick={() => {
                                const newFilters = filters.priority.filter(p => p !== priority);
                                setFilters(prev => ({ ...prev, priority: newFilters }));
                              }}
                              className="hover:text-gray-200"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        )) : filters.priority && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                            {t('jobs.priority')}: {t(`jobs.priority${filters.priority}`)}
                            <button
                              onClick={() => setFilters(prev => ({ ...prev, priority: [] }))}
                              className="hover:text-gray-200"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Jobs Count */}
            <div className="mb-2 text-xs sm:text-sm text-gray-400 px-4">
              {(() => {
                // Count relevant jobs - mora da koristi iste status kriterijume kao filteredJobs
                // (Finished poslovi ostaju vidljivi dok se ne arhiviraju), inače dobijemo "4 od 3".
                const relevantJobs = jobs?.filter(job => {
                  if (job.status === 'Archived' || job.status === 'Completed' || job.status === 'Cancelled') return false;
                  if (!job.serviceDate) return false;
                  return true;
                }) || [];
                
                return filteredJobs.length === 0 ? (
                  <span>{t('jobs.noJobsMatch')}</span>
                ) : (
                  <span>
                    {filteredJobs.length} {filteredJobs.length === 1 ? t('jobs.jobFound') : t('jobs.jobsFound')}
                    {relevantJobs.length !== filteredJobs.length && (
                      <span className="text-gray-500 ml-1">
                        ({t('jobs.outOf')} {relevantJobs.length})
                      </span>
                    )}
                  </span>
                );
              })()}
            </div>

          </>
        )}
        
        {showForm && (
          <div className="p-4 bg-gray-800 border-b border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">{t('jobs.newJob')}</h2>
              <button
                onClick={handleCloseForm}
                className="flex items-center px-3 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                <X className="h-4 w-4 mr-1" />
                {t('calendar.backToCalendar')}
              </button>
            </div>
            {selectedSlot && (
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={() => {
                    const newDate = moment(selectedSlot.date).subtract(1, 'day');
                    const formattedTime = selectedSlot.formattedTime;
                    setSelectedSlot({
                      date: newDate.toDate(),
                      formattedDate: newDate.format('YYYY-MM-DD'),
                      formattedTime,
                      displayDate: newDate.format('DD.MM.YYYY'),
                      displayTime: formattedTime
                    });
                  }}
                  className="p-2 rounded-full hover:bg-gray-700 text-white transition-colors"
                  aria-label="Prethodni dan"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div className="text-center">
                  <p className="text-sm text-gray-400">
                    {t('calendar.addingJobFor')} <span className="text-white font-medium">{selectedSlot.displayDate}</span> {t('calendar.at')} <span className="text-white font-medium">{selectedSlot.displayTime}</span>
                  </p>
                </div>
                <button
                  onClick={() => {
                    const newDate = moment(selectedSlot.date).add(1, 'day');
                    const formattedTime = selectedSlot.formattedTime;
                    setSelectedSlot({
                      date: newDate.toDate(),
                      formattedDate: newDate.format('YYYY-MM-DD'),
                      formattedTime,
                      displayDate: newDate.format('DD.MM.YYYY'),
                      displayTime: formattedTime
                    });
                  }}
                  className="p-2 rounded-full hover:bg-gray-700 text-white transition-colors"
                  aria-label="Sledeći dan"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Calendar Content or Form */}
      <div className="p-4 w-full">
        {!showForm ? (
          <>
            {calendarView === 'month' && renderMonthView()}
            {calendarView === 'week' && renderWeekView()}
            {calendarView === 'day' && renderDayView()}
            {calendarView === 'mobile' && renderMobileCardView()}
          </>
        ) : (
          <div className="w-full">
            <JobForm
              key={selectedSlot ? `${selectedSlot.formattedDate}-${selectedSlot.formattedTime}` : 'new-job'}
              isEdit={!!editingJob}
              jobData={editingJob || {
                serviceDate: selectedSlot?.formattedDate || '',
                scheduledTime: selectedSlot?.formattedTime || '',
                displayDateTime: selectedSlot ? `${selectedSlot.displayDate} ${selectedSlot.displayTime}` : ''
              }}
              selectedSlot={selectedSlot}
              onClose={() => {
                handleCloseForm();
                setEditingJob(null);
              }}
              isModal={false}
              className="w-full"
            />
          </div>
        )}
      </div>

      {errorMessage && (
        <div className="mt-4 p-3 bg-red-800 text-red-200 rounded-md flex items-center">
          <AlertCircle size={20} className="mr-2" />
          {errorMessage}
        </div>
      )}

      {/* Jobs Modal */}
      {showJobsModal && (modalDate || modalSingleJob) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black bg-opacity-50" onClick={() => {
          setShowJobsModal(false);
          setModalSingleJob(null);
        }}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gray-800 rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <div>
                <h2 className="text-lg font-semibold text-white">
                  {modalSingleJob 
                    ? `${t('jobs.job')} - ${formatDisplayValue(modalSingleJob.clientName)}`
                    : `${t('calendar.jobsFor')} ${modalDate.format('DD.MM.YYYY')}`
                  }
                </h2>
                {!modalSingleJob && (
                  <p className="text-sm text-gray-400 mt-1">
                    {t('calendar.jobCount')}: {getJobsForDate(modalDate).length}
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowJobsModal(false);
                  setModalSingleJob(null);
                }}
                className="p-2 hover:bg-gray-700 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-300" />
              </button>
            </div>
            <div className="p-6">
              {modalSingleJob ? (
                // Single job view (week view)
                (() => {
                  const job = modalSingleJob;
                  const isExpanded = expandedJobId === (job._id || 0);
                  return (
                    <div className="space-y-3">
                      <div
                        key={job._id || 0}
                        className="p-4 bg-gray-700 rounded-lg border border-gray-600"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {job.address && (job.address.street || job.address.city) ? (
                                <>
                                  <MapPin className="w-4 h-4 text-gray-400" />
                                  <div>
                                    <p className="text-sm font-medium text-white">
                                      {job.address.street || ''} {job.address.city || ''}
                                    </p>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <MapPin className="w-4 h-4 text-gray-400" />
                                  <div>
                                    <p className="text-sm font-medium text-white">
                                      {formatDisplayValue(job.clientName)}
                                    </p>
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-gray-400" />
                              <p className="text-sm text-gray-300">
                                {job.scheduledTime || '00:00'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => toggleJobDetails(job._id || 0)}
                              className="p-1.5 hover:bg-gray-600 rounded text-gray-300 hover:text-white transition-colors"
                              title={isExpanded ? t('common.hideDetails') : t('common.showDetails')}
                            >
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                            {(canEditJobs || canDeleteJobs) && (
                              <>
                                {canEditJobs && (
                                <button
                                  onClick={() => handleEditJob(job)}
                                  className="p-1.5 hover:bg-blue-600 rounded text-gray-300 hover:text-white transition-colors"
                                  title={t('jobs.editJob')}
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                )}
                                {canDeleteJobs && (
                                <button
                                  onClick={() => handleDeleteJob(job)}
                                  className="p-1.5 hover:bg-red-600 rounded text-gray-300 hover:text-white transition-colors"
                                  title={t('jobs.deleteJob')}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t border-gray-600 space-y-2">
                            <div>
                              <p className="text-xs text-gray-400 mb-1">{t('jobs.client')}</p>
                              <p className="text-sm text-white">{formatDisplayValue(job.clientName)}</p>
                            </div>
                            {job.serviceType && (
                              <div>
                                <p className="text-xs text-gray-400 mb-1">{t('jobs.serviceType')}</p>
                                <p className="text-sm text-gray-300">{job.serviceType}</p>
                              </div>
                            )}
                            {formatDisplayValue(job.issueDescription) !== '-' && (
                              <div>
                                <p className="text-xs text-gray-400 mb-1">{t('jobs.issueDescription')}</p>
                                <p className="text-sm text-gray-300">{formatDisplayValue(job.issueDescription)}</p>
                              </div>
                            )}
                            {job.priority && (
                              <div>
                                <p className="text-xs text-gray-400 mb-1">{t('jobs.priority')}</p>
                                <p className="text-sm text-gray-300">{job.priority}</p>
                              </div>
                            )}
                            {job.status && (
                              <div>
                                <p className="text-xs text-gray-400 mb-1">{t('jobs.status')}</p>
                                <p className="text-sm text-gray-300">{job.status}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()
              ) : getJobsForDate(modalDate).length > 0 ? (
                <div className="space-y-3">
                  {getJobsForDate(modalDate)
                    .sort((a, b) => {
                      const timeA = a.scheduledTime || '00:00';
                      const timeB = b.scheduledTime || '00:00';
                      return timeA.localeCompare(timeB);
                    })
                    .map((job, index) => {
                      const isExpanded = expandedJobId === (job._id || index);
                      return (
                        <div
                          key={job._id || index}
                          className="p-4 bg-gray-700 rounded-lg border border-gray-600"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                {job.address && (job.address.street || job.address.city) ? (
                                  <>
                                    <MapPin className="w-4 h-4 text-gray-400" />
                                    <div>
                                      <p className="text-sm font-medium text-white">
                                        {job.address.street || ''} {job.address.city || ''}
                                      </p>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <MapPin className="w-4 h-4 text-gray-400" />
                                    <div>
                                      <p className="text-sm font-medium text-white">
                                        {formatDisplayValue(job.clientName)}
                                      </p>
                                    </div>
                                  </>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-gray-400" />
                                <p className="text-sm text-gray-300">
                                  {job.scheduledTime || '00:00'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <button
                                onClick={() => toggleJobDetails(job._id || index)}
                                className="p-1.5 hover:bg-gray-600 rounded text-gray-300 hover:text-white transition-colors"
                                title={isExpanded ? t('common.hideDetails') : t('common.showDetails')}
                              >
                                {isExpanded ? (
                                  <ChevronUp className="w-4 h-4" />
                                ) : (
                                  <ChevronDown className="w-4 h-4" />
                                )}
                              </button>
                              {(canEditJobs || canDeleteJobs) && (
                                <>
                                  {canEditJobs && (
                                  <button
                                    onClick={() => handleEditJob(job)}
                                    className="p-1.5 hover:bg-blue-600 rounded text-gray-300 hover:text-white transition-colors"
                                    title={t('jobs.editJob')}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  )}
                                  {canDeleteJobs && (
                                  <button
                                    onClick={() => handleDeleteJob(job)}
                                    className="p-1.5 hover:bg-red-600 rounded text-gray-300 hover:text-white transition-colors"
                                    title={t('jobs.deleteJob')}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                          
                          {/* Expanded Details */}
                          {isExpanded && (
                            <div className="mt-4 pt-4 border-t border-gray-600 space-y-2">
                              <div>
                                <p className="text-xs text-gray-400 mb-1">{t('jobs.client')}</p>
                                <p className="text-sm text-white">{formatDisplayValue(job.clientName)}</p>
                              </div>
                              {job.serviceType && (
                                <div>
                                  <p className="text-xs text-gray-400 mb-1">{t('jobs.serviceType')}</p>
                                  <p className="text-sm text-gray-300">{job.serviceType}</p>
                                </div>
                              )}
                              {formatDisplayValue(job.issueDescription) !== '-' && (
                                <div>
                                  <p className="text-xs text-gray-400 mb-1">{t('jobs.issueDescription')}</p>
                                  <p className="text-sm text-gray-300">{formatDisplayValue(job.issueDescription)}</p>
                                </div>
                              )}
                              {job.priority && (
                                <div>
                                  <p className="text-xs text-gray-400 mb-1">{t('jobs.priority')}</p>
                                  <p className="text-sm text-gray-300">{job.priority}</p>
                                </div>
                              )}
                              {job.status && (
                                <div>
                                  <p className="text-xs text-gray-400 mb-1">{t('jobs.status')}</p>
                                  <p className="text-sm text-gray-300">{job.status}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                  <CalendarIcon className="w-12 h-12 mb-3" />
                  <p className="text-center">{t('calendar.noJobs')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView; 